import { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router";

import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { RadioButton } from "primereact/radiobutton";
import { InputText } from "primereact/inputtext";

import { PathsContext } from "@/context/PathsContext";
import { clonePath, generateUniqueName } from "@/api/paths";
import { useToast } from "sj-ui-toolkit/src/hooks";
import { AdminAppRoutes } from "sj-ui-toolkit/src/constants/routes";
import type { PathData } from "@/api/paths";

const MAX_NAME_LENGTH = 60;

interface ClonePathModalProps {
  visible: boolean;
  onHide: () => void;
  originalPath: PathData | null;
  existingPathNames?: string[];
}

const ClonePathModal = ({
  visible,
  onHide,
  originalPath,
  existingPathNames = [],
}: ClonePathModalProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [reassign, setReassign] = useState(false);
  const [cloneName, setCloneName] = useState("");
  const [nameValidationError, setNameValidationError] = useState("");
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { allPathNames, loadPathsAsync } = useContext(PathsContext);

  useEffect(() => {
    if (visible && originalPath?.name) {
      void loadPathsAsync();
      setCloneName(generateUniqueName(originalPath.name, existingPathNames, MAX_NAME_LENGTH));
    }
  }, [visible, originalPath?.name]);

  const nameExceedsLimit = cloneName.trim().length > MAX_NAME_LENGTH;

  const handleClone = async () => {
    if (nameExceedsLimit) return;

    const { validatePathName } = await import("@/api/paths");
    const validation = validatePathName(cloneName, allPathNames, null);
    if (!validation.isValid) {
      setNameValidationError(validation.message);
      return;
    }

    setIsLoading(true);
    try {
      const response = await clonePath(originalPath!, reassign, cloneName.trim(), showToast);
      const newPathId = response?.role?.id;
      if (newPathId) {
        navigate(AdminAppRoutes.routes.paths.routes.edit.route.replace(":id", String(newPathId)));
      }
      onHide();
    } catch {
      // error already toasted inside clonePath
    } finally {
      setIsLoading(false);
    }
  };

  const handleHide = () => {
    setCloneName("");
    setReassign(false);
    setNameValidationError("");
    onHide();
  };

  const footer = (
    <div className="flex justify-end gap-3">
      <Button
        label="Cancel"
        icon="pi pi-times"
        onClick={handleHide}
        severity="secondary"
        disabled={isLoading}
      />
      <Button
        label="Clone"
        icon="pi pi-clone"
        onClick={() => void handleClone()}
        disabled={isLoading || !cloneName.trim() || nameExceedsLimit}
        loading={isLoading}
      />
    </div>
  );

  return (
    <Dialog
      header="Clone Path"
      visible={visible}
      onHide={handleHide}
      style={{ width: "600px" }}
      footer={footer}
      maskStyle={{ backdropFilter: "blur(5px)" }}
    >
      <div className="space-y-6">
        <div className="flex flex-col gap-2">
          <label htmlFor="clone-name" className="atlas-h5 font-semibold">
            Name
          </label>
          <InputText
            id="clone-name"
            value={cloneName}
            onChange={e => {
              setCloneName(e.target.value);
              setNameValidationError("");
            }}
            className={`w-full ${nameExceedsLimit ? "p-invalid" : ""}`}
            disabled={isLoading}
            aria-invalid={!!nameValidationError}
            aria-describedby={nameValidationError ? "clone-name-error" : undefined}
          />
          <small className={nameExceedsLimit ? "error-text" : "text-color-secondary"}>
            {cloneName.trim().length}/{MAX_NAME_LENGTH} characters
          </small>
          {nameValidationError && (
            <small id="clone-name-error" role="alert" className="error-text">
              {nameValidationError}
            </small>
          )}
        </div>

        <div>
          <h3 className="atlas-h5 font-semibold mb-4">
            Would you like to require learners to re-take previously completed lessons?
          </h3>
          <p className="body-sm text-secondary mb-6">
            Please note: this setting cannot be removed from your path once enabled.
          </p>

          <div className="space-y-6">
            <div className="flex items-center">
              <RadioButton
                inputId="allow-retake"
                name="retakeOption"
                value={true}
                onChange={e => setReassign(e.value as boolean)}
                checked={reassign === true}
              />
              <label htmlFor="allow-retake" className="ml-2">
                <span className="body-md">
                  Yes, require learners to re-take previously completed lessons.
                </span>
              </label>
            </div>

            <div className="flex items-center">
              <RadioButton
                inputId="prevent-retake"
                name="retakeOption"
                value={false}
                onChange={e => setReassign(e.value as boolean)}
                checked={reassign === false}
              />
              <label htmlFor="prevent-retake" className="ml-2">
                <span className="body-md">No, count previously completed lessons as complete.</span>
              </label>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default ClonePathModal;
